/**
 * ConfirmPH: Owner Trust-badges panel.
 *
 * Shows the badges this business has earned + actionable suggestions for
 * the missing ones. Data is fetched from `/api/trust-badges/me` so the
 * frontend can't invent or alter it.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ShieldCheck, CheckCircle2, Star, FileText, Activity, MessageSquare,
  Smartphone, Loader2, Lightbulb, AlertOctagon,
} from "lucide-react";

const ICONS = {
  profile_completed:     CheckCircle2,
  active_on_confirmph:   Activity,
  in_app_booking:        Smartphone,
  verified_reviews:      Star,
  registration_evidence_reviewed: FileText,
  fleet_registration_reviewed:    FileText,
  fleet_insurance_reviewed:       ShieldCheck,
  vehicle_registration_reviewed:  FileText,
  vehicle_insurance_reviewed:     ShieldCheck,
  no_critical_alerts:    ShieldCheck,
  responds_to_inquiries: MessageSquare,
};

const TONES = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  amber:   "bg-amber-50 border-amber-200 text-amber-900",
  slate:   "bg-slate-50 border-slate-200 text-slate-800",
  indigo:  "bg-indigo-50 border-indigo-200 text-indigo-900",
};

export default function TrustBadgesPanel() {
  const [state, setState] = useState({ loading: true, badges: [], suggestions: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/trust-badges/me");
        if (!cancelled) setState({ loading: false, ...data });
      } catch (_) {
        if (!cancelled) setState({ loading: false, badges: [], suggestions: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500"
           data-testid="trust-badges-panel-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading profile signals…
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="trust-badges-panel">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            Profile signals on your public profile
          </h3>
          <p className="text-xs text-slate-500 mt-1 max-w-prose">
            These badges are computed from real ConfirmPH data and shown on your
            public business page. They are <em>signals</em>, not a guarantee of
            safety, legality, or current compliance.
          </p>
        </div>
        <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
              data-testid="trust-badges-earned-count">
          {state.badges.length} earned
        </span>
      </div>

      {state.badges.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="trust-badges-earned-list">
          {state.badges.map((b) => {
            const Icon = ICONS[b.key] || CheckCircle2;
            const tone = TONES[b.tone] || TONES.slate;
            return (
              <div key={b.key}
                   className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium " + tone}
                   title={b.description || ""}
                   data-testid={`tbp-badge-${b.key}`}>
                <Icon className="w-3.5 h-3.5" /> {b.label}
              </div>
            );
          })}
        </div>
      )}

      {state.suggestions.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 inline-flex items-center gap-1.5">
            <Lightbulb className="w-3.5 h-3.5" /> Earn more
          </div>
          <ul className="mt-2 space-y-2" data-testid="trust-badges-suggestions">
            {state.suggestions.map((s) => (
              <li key={s.key}
                  className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm"
                  data-testid={`tbp-suggestion-${s.key}`}>
                <div className="font-medium">{s.title}</div>
                <div className="text-xs text-slate-600 mt-0.5">{s.tip}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 inline-flex items-start gap-1.5"
           data-testid="trust-badges-disclaimer">
        <AlertOctagon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        Profile signals show what you have done on ConfirmPH. They do not say a
        business is safe, legal, or scam-free, and ConfirmPH does not guarantee
        document validity or current compliance. Customers should still do their
        own due diligence.
      </div>
    </div>
  );
}
